import React from 'react';
import ScheduleCell from './ScheduleCell';
import { ROW_GLOW_CLASSES } from './schedule-colors';
import { CrewAssignment, ScheduleEvent, OOOEntry, EventColorEntry } from './types';
import { DayBar, BlockBar } from '../stores/useScheduleStore';
import { isWeekend } from './day-utils';

/**
 * A grid column. `date` is a full 'YYYY-MM-DD' rather than a day-of-month integer:
 * the window can span months, so an integer would need a month alongside it to
 * mean anything, and that pairing is what item 9 removed.
 */
export type DayColumn = { type: 'day'; date: string } | { type: 'collapse'; count: number };

interface ScheduleRowProps {
  staff: { id: string; firstname: string; lastname: string };
  dayColumns: DayColumn[];
  /** Per-day bars for this user, already merged with pending changes upstream. */
  barsByDay: Map<string, DayBar[]>;
  /** Per-day unavailability block for this user (at most one block per day). */
  blocksByDay: Map<string, BlockBar>;
  eventsPerDay: Map<string, ScheduleEvent[]>;
  oooPerDay: Map<string, OOOEntry[]>;
  eventColorMap: Map<string, EventColorEntry>;
  eventLookup: Map<string, ScheduleEvent>;
  /** Today as 'YYYY-MM-DD'. Passed down rather than read per row so every cell
   *  compares against the same value and memoization holds. */
  today: string;
  /** Threaded straight through to every cell. */
  readOnly?: boolean;
  monthlyCount: number;
  trailingCount: number;
  isRowHovered: boolean;
  hoveredDate: string | null;
  onCellHover: (userId: string | null, date: string | null) => void;
  onCellClick: (userId: string, dateStr: string, rect: DOMRect, eventId?: string) => void;
  onDragStart: (e: React.DragEvent, assignment: CrewAssignment, userId: string, shiftRange?: { start: string; end: string }) => void;
  onDrop: (e: React.DragEvent, targetUserId: string, targetDateStr: string) => void;
  onDragOver: (e: React.DragEvent) => void;
}

const EMPTY_BARS: DayBar[] = [];

const ScheduleRow: React.FC<ScheduleRowProps> = React.memo(({
  staff, dayColumns, barsByDay, blocksByDay,
  eventsPerDay, oooPerDay, eventColorMap, eventLookup,
  today, readOnly = false, monthlyCount, trailingCount,
  isRowHovered, hoveredDate, onCellHover,
  onCellClick, onDragStart, onDrop, onDragOver,
}) => {
  return (
    <>
      {/* Sticky name cell — grows + glows when ANY cell in this row is hovered */}
      <div className={`sticky left-0 z-10 bg-gray-900 day:bg-white border-r border-b border-gray-700/60 day:border-gray-200 px-2 py-1
        flex items-center text-sm text-on-surface day:text-gray-900 truncate font-medium min-w-[160px]
        transition-all duration-150 ease-out origin-left
        ${isRowHovered
          ? 'scale-[1.04] bg-primary/10 day:bg-amber-100 text-white day:text-amber-900 ring-1 ring-inset ring-primary/40 day:ring-amber-500/60 shadow-[0_0_24px_-6px_rgba(255,204,51,0.55)] z-20'
          : ''}`}>
        {staff.firstname} {staff.lastname}
      </div>

      {/* Day cells + collapse indicators */}
      {dayColumns.map((col, i) => {
        if (col.type === 'collapse') {
          return (
            <div key={`collapse-${i}`}
              className={`border-r border-b border-gray-800/60 day:border-gray-200 bg-gray-800/10 day:bg-gray-50 ${isRowHovered ? ROW_GLOW_CLASSES : ''}`} />
          );
        }
        const ds = col.date;
        return (
          <ScheduleCell
            key={ds}
            dateStr={ds}
            userId={staff.id}
            bars={barsByDay.get(ds) || EMPTY_BARS}
            blockBar={blocksByDay.get(ds) || null}
            eventsOnDay={eventsPerDay.get(ds) || []}
            oooEntries={oooPerDay.get(ds) || []}
            eventColorMap={eventColorMap}
            eventLookup={eventLookup}
            isWeekend={isWeekend(ds)}
            isToday={today === ds}
            isRowHovered={isRowHovered}
            isColHovered={hoveredDate === ds}
            readOnly={readOnly}
            onCellHover={onCellHover}
            onCellClick={onCellClick}
            onDragStart={onDragStart}
            onDrop={onDrop}
            onDragOver={onDragOver}
          />
        );
      })}

      {/* Show count cell (sticky right) */}
      <div className={`sticky right-0 z-10 bg-gray-900 day:bg-white border-l border-b border-gray-700/60 day:border-gray-200 px-2 py-1
        flex items-center justify-center text-xs text-on-surface-variant day:text-gray-700 min-w-[60px]
        transition-colors duration-150 ${isRowHovered ? ROW_GLOW_CLASSES : ''}`}>
        {/* Producer workload figures. The crew payload does not carry showCounts
            at all, so rendering 0/0 there would be an invented fact. */}
        {!readOnly && (
          <>
            <span className="text-on-surface day:text-gray-900 font-medium">{monthlyCount}</span>
            <span className="mx-0.5">/</span>
            <span className="text-on-surface-variant day:text-gray-500">{trailingCount}</span>
          </>
        )}
      </div>
    </>
  );
});

ScheduleRow.displayName = 'ScheduleRow';
export default ScheduleRow;
